import React, { CSSProperties, FC, PropsWithChildren, useRef } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import ConditionalGoogleMapsProvider from '../../../../components/ConditionalGoogleMapsProvider';
import useScrollTopOnLocationChange from '@app/hooks/useScrollTopOnLocationChange';
import SupplierRegistrationMode from '@supplierRegistration/supplierRegistrationMode';
import useInfraStores from '@app/hooks/useInfraStores';
import SupplierRegistrationFooter from '@supplierRegistration/routes/app/SupplierRegistrationFooter';
import useSupplierRegistrationCustomerParameters from '@supplierRegistration/hooks/useSupplierRegistrationCustomerData';
import Loader from '@app/components/Loader';
import useAppStores from '@app/hooks/useAppStores';
import config from '@app/config';
import Log from '@app/libs/logger';
import ReCAPTCHA from 'react-google-recaptcha';

const SupplierRegistrationAppContent: FC<PropsWithChildren> = observer((props) => {
  const { children } = props;

  const { languageStore, navigationStore } = useInfraStores<SupplierRegistrationMode>();
  const { supplierRegistrationCustomerDataStore } = useAppStores<SupplierRegistrationMode>();

  const appContentContainerRef = useRef<HTMLDivElement | null>(null);
  useScrollTopOnLocationChange(appContentContainerRef);

  const pageDirection = languageStore.isRTL ? 'rtl' : 'ltr';

  useSupplierRegistrationCustomerParameters(!navigationStore.isIpsPage);

  const { currentConfiguration, currentAsyncProcessMaskedData } = supplierRegistrationCustomerDataStore;
  if (currentConfiguration.isNotSettled() || currentAsyncProcessMaskedData?.isNotSettled()) {
    return <Loader spinning fullScreen />;
  }

  const shouldUseCaptcha = config.featureFlags?.supplierRegistrationCaptcha; // do NOT add && window['grecaptcha']

  return (
    <ConditionalGoogleMapsProvider>
      <PageLayout direction={pageDirection}>
        {shouldUseCaptcha && (
          <ReCAPTCHA
            sitekey={config.supplierRegistration.captcha.siteKey}
            size='invisible'
            ref={supplierRegistrationCustomerDataStore.getCaptchaRef()}
            onErrored={(): void => {
              const scriptTag = document.querySelector('script[src*="recaptcha"]');
              const isOnline = navigator.onLine;

              if (!isOnline) {
                Log.exception(new Error('reCAPTCHA failed to load: device is offline'));
                return;
              }

              if (!scriptTag) {
                Log.exception(new Error('reCAPTCHA failed to load: script tag was never injected into DOM'));
                return;
              }

              // Script tag exists and device is online — likely blocked by an ad blocker or CSP.
              // Do a fetch probe to distinguish a network/CDN failure from a client-side block.
              fetch('https://www.google.com/recaptcha/api.js', { method: 'HEAD', mode: 'no-cors' })
                .then(() => {
                  // no-cors fetch succeeded — the URL is reachable, so the block is client-side (ad blocker / CSP)
                  Log.exception(
                    new Error('reCAPTCHA failed to load: script reachable but blocked client-side (ad blocker or CSP)'),
                  );
                })
                .catch(() => {
                  Log.exception(new Error('reCAPTCHA failed to load: network or CDN failure reaching google.com/recaptcha'));
                });
            }}
          />
        )}
        <ContentLayout ref={appContentContainerRef}>
          <ContentContainer id='main'>{children}</ContentContainer>
          <StyledFooter />
        </ContentLayout>
      </PageLayout>
    </ConditionalGoogleMapsProvider>
  );
});

export default SupplierRegistrationAppContent;

const ContentLayout = styled.main`
  flex: 1 1 auto;
  overflow: auto;
  padding-bottom: 18px;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ContentContainer = styled.div`
  padding-bottom: 32px;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const StyledFooter = styled(SupplierRegistrationFooter)`
  flex: 0 0 auto;
  text-align: center;
`;

const PageLayout = styled.div<{ direction?: CSSProperties['direction'] }>`
  display: flex;
  flex-direction: row;
  flex: auto;
  height: 100vh;
  min-height: 0;

  direction: ${(p): CSSProperties['direction'] => p.direction ?? 'ltr'};

  font-family: var(--title-font-family);

  .grecaptcha-badge {
    margin-bottom: 50px !important;
  }
`;
